import React, { useEffect, useState } from 'react';
import { Modal, Table } from 'react-bootstrap';
import { Keyboard } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { ScheduleToolbarButton } from './ScheduleToolbarButton';
import {
  SCHEDULE_KEYBOARD_SHORTCUT_GROUPS,
  SCHEDULE_OPEN_KEYBOARD_SHORTCUTS_EVENT,
} from './scheduleKeyboardShortcuts';
import {
  isScheduleHistoryKeyTarget,
  isScheduleOpenShortcutsKey,
} from './scheduleLayoutHooks';
import './ScheduleKeyboardShortcutsModal.scss';

const prefix = 'creativeDetail-';

function ShortcutKeyCombo({ combo }: { combo: string }) {
  const parts = combo.split(/\s*\+\s*/).filter((part) => part.length > 0);
  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={`${part}-${index}`}>
          {index > 0 ? (
            <span className="schedule-keyboard-shortcuts-modal__key-sep" aria-hidden>
              +
            </span>
          ) : null}
          <kbd>{part}</kbd>
        </React.Fragment>
      ))}
    </>
  );
}

function ShortcutKeys({ keysLabel }: { keysLabel: string }) {
  const alternatives = keysLabel.split(/\s*\/\s*/);
  return (
    <span className="schedule-keyboard-shortcuts-modal__keys">
      {alternatives.map((combo, index) => (
        <React.Fragment key={`${combo}-${index}`}>
          {index > 0 ? (
            <span className="schedule-keyboard-shortcuts-modal__key-sep" aria-hidden>
              /
            </span>
          ) : null}
          <ShortcutKeyCombo combo={combo.trim()} />
        </React.Fragment>
      ))}
    </span>
  );
}

type ScheduleKeyboardShortcutsModalBodyProps = {
  show: boolean;
  onClose: () => void;
};

const ScheduleKeyboardShortcutsModalBody = ({
  show,
  onClose,
}: ScheduleKeyboardShortcutsModalBodyProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      scrollable
      className="schedule-keyboard-shortcuts-modal"
      aria-labelledby="schedule-keyboard-shortcuts-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="schedule-keyboard-shortcuts-title">
          {t(prefix + 'scheduleShortcutsTitle')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="schedule-keyboard-shortcuts-modal__mod-note">
          {t(prefix + 'scheduleShortcutsModNote')}
        </p>
        {SCHEDULE_KEYBOARD_SHORTCUT_GROUPS.map((group) => (
          <section key={group.sectionKey} className="schedule-keyboard-shortcuts-modal__section">
            <h3 className="schedule-keyboard-shortcuts-modal__section-title">
              {t(prefix + group.sectionKey)}
            </h3>
            <Table borderless size="sm" className="schedule-keyboard-shortcuts-modal__table">
              <tbody>
                {group.items.map((item) => (
                  <tr key={item.descriptionKey}>
                    <td>{t(prefix + item.descriptionKey)}</td>
                    <td>
                      <ShortcutKeys keysLabel={t(prefix + item.keysKey)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </section>
        ))}
      </Modal.Body>
    </Modal>
  );
};

export const ScheduleKeyboardShortcutsModal = React.memo(function ScheduleKeyboardShortcutsModal() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const open = () => setShow(true);
    window.addEventListener(SCHEDULE_OPEN_KEYBOARD_SHORTCUTS_EVENT, open);
    return () => window.removeEventListener(SCHEDULE_OPEN_KEYBOARD_SHORTCUTS_EVENT, open);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isScheduleHistoryKeyTarget(e.target)) return;
      if (!isScheduleOpenShortcutsKey(e)) return;
      e.preventDefault();
      setShow(true);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  return (
    <>
      <ScheduleToolbarButton
        aria-label={t(prefix + 'scheduleShortcutsButton')}
        title={t(prefix + 'scheduleShortcutsButtonTooltip')}
        onClick={() => setShow(true)}
        icon={<Keyboard size={18} aria-hidden />}
        labelClassName="d-none d-lg-inline small"
      >
        {t(prefix + 'scheduleShortcutsButton')}
      </ScheduleToolbarButton>
      {show ? <ScheduleKeyboardShortcutsModalBody show onClose={() => setShow(false)} /> : null}
    </>
  );
});
